import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const PurchaseInvoicesList = () => {
  // Mock data for purchase invoices - replace with actual data from API/state
  const [purchaseInvoices] = useState([
    {
      id: 1,
      poNumber: 'PO-2026-001',
      supplierName: 'Emirates Industrial Supply Co.',
      date: '2026-03-15',
      dueDate: '2026-04-15',
      total: 5500.00,
      status: 'Pending',
      items: 15
    },
    {
      id: 2,
      poNumber: 'PO-2026-002',
      supplierName: 'Global Trading Partners',
      date: '2026-03-10',
      dueDate: '2026-03-25',
      total: 3200.00,
      status: 'Received',
      items: 8
    },
    {
      id: 3,
      poNumber: 'PO-2026-003',
      supplierName: 'Middle East Manufacturing Ltd',
      date: '2026-02-28',
      dueDate: '2026-03-30',
      total: 7800.00,
      status: 'Paid',
      items: 22
    },
    {
      id: 4,
      poNumber: 'PO-2026-004',
      supplierName: 'Dubai Commercial Solutions',
      date: '2026-03-18',
      dueDate: '2026-04-18',
      total: 2100.00,
      status: 'Pending',
      items: 6
    },
    {
      id: 5,
      poNumber: 'PO-2026-005',
      supplierName: 'Abu Dhabi Trading Group',
      date: '2026-03-12',
      dueDate: '2026-03-26',
      total: 4500.00,
      status: 'Received',
      items: 11
    }
  ]);

  // Individual selection state for each invoice
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());

  // Handle individual invoice selection
  const handleInvoiceSelect = (invoiceId) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedInvoices.size === purchaseInvoices.length) {
      // If all are selected, deselect all
      setSelectedInvoices(new Set());
    } else {
      // Select all
      setSelectedInvoices(new Set(purchaseInvoices.map(inv => inv.id)));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'Paid': 'badge-success',
      'Received': 'badge-info',
      'Pending': 'badge-warning',
      'Cancelled': 'badge-error'
    };
    return `badge ${statusClasses[status] || 'badge-ghost'} badge-sm`;
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && new Date().toDateString() !== new Date(dueDate).toDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Purchase History</h2>
          <p className="text-gray-600">Track all your purchase orders and invoices</p>
        </div>
        <Link to="/purchases/orders/create" className="btn btn-primary">
          ➕ Create Purchase Order
        </Link>
      </div>

      {/* Selection Actions */}
      {selectedInvoices.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedInvoices.size} purchase order{selectedInvoices.size > 1 ? 's' : ''} selected
            </span>
            <div className="space-x-2">
              <button className="btn btn-sm btn-outline btn-blue">Print Selected</button>
              <button className="btn btn-sm btn-outline btn-green">Email Selected</button>
              <button className="btn btn-sm btn-outline btn-red">Delete Selected</button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    checked={selectedInvoices.size === purchaseInvoices.length && purchaseInvoices.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>PO Number</th>
                <th>Supplier</th>
                <th>Date</th>
                <th>Due Date</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchaseInvoices.map((invoice) => (
                <tr 
                  key={invoice.id} 
                  className={`hover:bg-gray-50 ${isOverdue(invoice.dueDate) && invoice.status !== 'Paid' ? 'bg-red-50' : ''}`}
                >
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary"
                      checked={selectedInvoices.has(invoice.id)}
                      onChange={() => handleInvoiceSelect(invoice.id)}
                    />
                  </td>
                  <td className="font-medium text-primary">{invoice.poNumber}</td>
                  <td>{invoice.supplierName}</td>
                  <td>{new Date(invoice.date).toLocaleDateString()}</td>
                  <td>
                    <span className={isOverdue(invoice.dueDate) && invoice.status !== 'Paid' ? 'text-red-600 font-semibold' : ''}>
                      {new Date(invoice.dueDate).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="text-center badge badge-outline">{invoice.items}</td>
                  <td className="font-mono">{formatCurrency(invoice.total)}</td>
                  <td>
                    <span className={getStatusBadge(invoice.status)}>
                      {invoice.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      <button className="btn btn-xs btn-ghost">👁️ View</button>
                      <button className="btn btn-xs btn-ghost">📄 PDF</button>
                      <button className="btn btn-xs btn-ghost">✉️ Email</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {purchaseInvoices.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📋</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No purchase orders yet</h3>
            <p className="text-gray-600 mb-4">Create your first purchase order to get started</p>
            <Link to="/purchases/orders/create" className="btn btn-primary">
              Create Your First Purchase Order
            </Link>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600 mb-2">Total Purchase Orders</div>
          <div className="text-3xl font-bold text-gray-900">{purchaseInvoices.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600 mb-2">Total Amount</div>
          <div className="text-3xl font-bold text-gray-900">
            {formatCurrency(purchaseInvoices.reduce((sum, inv) => sum + inv.total, 0))}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600 mb-2">Pending</div>
          <div className="text-3xl font-bold text-orange-600">
            {purchaseInvoices.filter(inv => inv.status === 'Pending').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600 mb-2">Paid</div>
          <div className="text-3xl font-bold text-green-600">
            {purchaseInvoices.filter(inv => inv.status === 'Paid').length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseInvoicesList;
